import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Volume2, VolumeX } from 'lucide-react';
export function AudioPlayer() {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showHint, setShowHint] = useState(true);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = 0.35;
    audio.loop = true;
    audio.muted = true;
    // Attempt autoplay muted (browsers allow this)
    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise.then(() => setIsPlaying(true)).catch(() => {
        // Autoplay blocked — wait for user interaction
        const unlock = () => {
          audio.play().then(() => setIsPlaying(true)).catch(() => {});
          document.removeEventListener('click', unlock);
          document.removeEventListener('scroll', unlock);
        };
        document.addEventListener('click', unlock);
        document.addEventListener('scroll', unlock);
      });
    }
    // Hide hint after 5 seconds
    const timer = setTimeout(() => setShowHint(false), 5000);
    return () => clearTimeout(timer);
  }, []);
  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isMuted) {
      audio.muted = false;
      setIsMuted(false);
      // If not playing yet, start
      if (!isPlaying) {
        audio.play().then(() => setIsPlaying(true)).catch(() => {});
      }
    } else {
      audio.muted = true;
      setIsMuted(true);
    }
    setShowHint(false);
  };
  return <>
      {/* Hidden audio element — swap src for your preferred track */}
      <audio ref={audioRef} src="https://cdn.pixabay.com/download/audio/2022/11/22/audio_febc508520.mp3" preload="auto" data-id="element-10" />

      {/* Floating button */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2" data-id="element-11">
        {/* Hint tooltip */}
        <AnimatePresence data-id="element-12">
          {showHint && <motion.div initial={{
          opacity: 0,
          y: 8,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 8,
          scale: 0.95
        }} transition={{
          duration: 0.3
        }} className="bg-[#1a1a1a] border border-[#C9A84C]/40 text-[#C9A84C] text-xs font-medium px-3 py-2 rounded-sm tracking-wide whitespace-nowrap" data-id="element-13">
              🎵 Activar música
            </motion.div>}
        </AnimatePresence>

        {/* Toggle button */}
        <motion.button onClick={toggleMute} whileHover={{
        scale: 1.1
      }} whileTap={{
        scale: 0.95
      }} className="w-11 h-11 rounded-full bg-[#0a0a0a] border border-[#C9A84C]/50 hover:border-[#C9A84C] flex items-center justify-center transition-colors duration-200 shadow-[0_0_15px_rgba(201,168,76,0.2)] hover:shadow-[0_0_20px_rgba(201,168,76,0.4)]" title={isMuted ? 'Activar música' : 'Silenciar música'} data-id="element-14">
          {isMuted ? <VolumeX className="w-4 h-4 text-gray-500" data-id="element-15" /> : <Volume2 className="w-4 h-4 text-[#C9A84C]" data-id="element-16" />}
        </motion.button>

        {/* Animated bars when playing & unmuted */}
        <AnimatePresence data-id="element-17">
          {!isMuted && isPlaying && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="flex items-end gap-[3px] h-4" data-id="element-18">
              {[0.6, 1, 0.7, 0.9, 0.5].map((h, i) => <motion.span key={i} className="w-[3px] bg-[#C9A84C] rounded-full" animate={{
            scaleY: [h, 1, h * 0.4, 1, h]
          }} transition={{
            duration: 1.2,
            repeat: Infinity,
            delay: i * 0.15,
            ease: 'easeInOut'
          }} style={{
            height: '100%',
            transformOrigin: 'bottom'
          }} data-id="element-19" />)}
            </motion.div>}
        </AnimatePresence>
      </div>
    </>;
}